import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type ExportStatus = 'COMPLETED' | 'IN_PROGRESS' | 'AWAITING';

export interface ExportFolder {
  id: string;
  name: string;
  createdAt: string;
  exportStatus: ExportStatus;
}

export interface TriggerPayload {
  period: string;
}

export interface KeyPayload {
  key: string;
}

interface InitialState {
  isLoadingFolders: boolean;
  folders: ExportFolder[] | null;

  isTriggering: boolean;
  lastTriggeredPeriod: string | null;

  isDeleting: boolean;
  lastDeletedKey: string | null;

  isDownloading: boolean;
  lastDownloadedKey: string | null;

  error: string | null;
}

const initialState: InitialState = {
  isLoadingFolders: false,
  folders: null,

  isTriggering: false,
  lastTriggeredPeriod: null,

  isDeleting: false,
  lastDeletedKey: null,

  isDownloading: false,
  lastDownloadedKey: null,

  error: null,
};

const exportDump = createSlice({
  name: 'exportDump',
  initialState,
  reducers: {
    requestFolders: (state): void => {
      state.isLoadingFolders = true;
      state.error = null;
    },
    getFoldersSuccess: (state, { payload }: PayloadAction<ExportFolder[]>): void => {
      state.isLoadingFolders = false;
      state.folders = payload;
    },
    getFoldersError: (state, { payload }: PayloadAction<string>): void => {
      state.isLoadingFolders = false;
      state.error = payload;
    },

    requestTrigger: (state, _action: PayloadAction<TriggerPayload>): void => {
      state.isTriggering = true;
      state.error = null;
      state.lastTriggeredPeriod = null;
    },
    triggerSuccess: (state, { payload }: PayloadAction<TriggerPayload>): void => {
      state.isTriggering = false;
      state.lastTriggeredPeriod = payload.period;
    },
    triggerError: (state, { payload }: PayloadAction<string>): void => {
      state.isTriggering = false;
      state.error = payload;
    },

    requestDelete: (state, _action: PayloadAction<KeyPayload>): void => {
      state.isDeleting = true;
      state.error = null;
      state.lastDeletedKey = null;
    },
    deleteSuccess: (state, { payload }: PayloadAction<KeyPayload>): void => {
      state.isDeleting = false;
      state.lastDeletedKey = payload.key;
      if (state.folders) {
        state.folders = state.folders.filter((f) => f.name !== payload.key);
      }
    },
    deleteError: (state, { payload }: PayloadAction<string>): void => {
      state.isDeleting = false;
      state.error = payload;
    },

    requestDownload: (state, _action: PayloadAction<KeyPayload>): void => {
      state.isDownloading = true;
      state.error = null;
      state.lastDownloadedKey = null;
    },
    downloadSuccess: (state, { payload }: PayloadAction<KeyPayload>): void => {
      state.isDownloading = false;
      state.lastDownloadedKey = payload.key;
    },
    downloadError: (state, { payload }: PayloadAction<string>): void => {
      state.isDownloading = false;
      state.error = payload;
    },

    clearError: (state): void => {
      state.error = null;
    },
  },
});

export const {
  requestFolders,
  getFoldersSuccess,
  getFoldersError,
  requestTrigger,
  triggerSuccess,
  triggerError,
  requestDelete,
  deleteSuccess,
  deleteError,
  requestDownload,
  downloadSuccess,
  downloadError,
  clearError,
} = exportDump.actions;

export default exportDump.reducer;
